/**
 * deployment-edit-date.js
 */

import React from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { renderForMobileContext } from 'utils/app-context';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import addYears from 'date-fns/addYears';
import { DogsContext } from 'contexts/dogs/dogs-context';


const useStyles = makeStyles(theme => ({
  alert: {
    marginTop: theme.spacing(1)
  }
}));


export default function DeploymentEditDate(props) {
  const {
    deploymentId,
    drawerState,
    edit,
  } = props;
  const classes = useStyles();
  const translation = edit.translation;
  const { t } = useTranslation(translation);
  const { getActiveDogs } = React.useContext(DogsContext);
  const { setValue, control } = useFormContext();
  const dateValue = useWatch({ control, name: 'date' });
  const noActiveDogsForDate = React.useMemo(() => getActiveDogs(dateValue).length === 0, [getActiveDogs, dateValue]);


  // if mobile and creating, use current time
  React.useEffect(() => {
    if (deploymentId === 0 && renderForMobileContext()) {
      setValue('date', new Date());
    }
  }, [deploymentId, setValue]);


  return (
    <>
      <EditorIconSection
        iconName='event'
        helpProps={{
          translation,
          section: 'dateTime'
        }}
      >
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('dateTime.dateTime.helpTitle')}
          text={t('dateTime.dateTime.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem md={7}>
                <DateTimePicker
                  variant='datetime'
                  name='date'
                  label={t('dateTime.dateTime.label')}
                  focusHandlers={focusHandlers}
                  format="MM/dd/yyyy HH:mm"
                  showTodayButton={true}
                  rules={{ required: t('dateTime.dateTime.errMsgEmpty')}}
                  maxDate={addYears(new Date(), 1)}
                  maxDateMessage={t('dateTime.dateTime.errMsgMaxDate')}
                  minDate={addYears(new Date(), -20)}
                  minDateMessage={t('dateTime.dateTime.errMsgMinDate')}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>

        {noActiveDogsForDate &&
        <Alert severity='warning' className={classes.alert}>
          <Typography>
            You don't have any dogs configured for this deployment date and won't be able to complete it.
            To fix this, modify the deployment date to match one of your active dogs or modify the Record Keeping Start Date and/or End Date in your Dog settings to match this deployment.
          </Typography>
        </Alert>}

      </EditorIconSection>
    </>
  );
}